import type {
  CreateVideoRequest,
  PublishVideoRequest,
  Video,
  Segment,
} from "@vms/tubeforge-shared";
import { apiGet, apiPost, apiDelete, apiRequest } from "./client.ts";
import { w } from "@/lib/words.ts";

/** Session 6 W1a.1: a rendered video's finishing state — computed by the engine (one rule: the newest finishing run that
 *  was not stopped is the current cut; a newer one in progress means the earlier approved cut is history). The list
 *  carries it on rendered rows; the Post dialog asks for it with the history. */
export type FinishingStateName = "awaiting_finishing" | "finishing" | "awaiting_delivery" | "newer_in_progress" | "ready";
export interface FinishingCut { status: string; createdAt: string; delivery: "none" | "awaiting" | "approved" | "rejected" }
export interface VideoFinishing {
  state: FinishingStateName; postable: boolean; reason: string | null;
  current: FinishingCut | null; history: FinishingCut[];
  /** Session 6 W1b: the current cut's file — what the download gives (null until the cut exists). */
  cutUrl: string | null;
}
export type VideoWithFinishing = Video & { finishing?: VideoFinishing;
  /** (B112, 0066) the material changes at gate_final from the held_at_gate callback; null: none known (no badge) */
  materialCount?: number | null };
/** (B107) each label read through w() when shown (Admin → Words): page.finishing.<state> */
export const FINISHING_LABEL: Record<FinishingStateName, string> = {
  get awaiting_finishing() { return w("page.finishing.awaiting_finishing"); },
  get finishing() { return w("page.finishing.finishing"); },
  get awaiting_delivery() { return w("page.finishing.awaiting_delivery"); },
  get newer_in_progress() { return w("page.finishing.newer_in_progress"); },
  get ready() { return w("page.finishing.ready"); },
};

export const videosApi = {
  /** (tf3-34b) the audience-retention curve of the owner's own video, one per published post (YouTube reports one) */
  // (tf3-35a) each posted version's numbers per platform (owner only)
  postNumbers: (id: string) => apiGet<import("../lib/post-numbers.ts").PostNumbersAnswer>(`/videos/${id}/post-numbers`),
  /** (B112; C2.5-4) the final review's list of material changes for the owner's own video (or why it isn't readable) */
  material: (id: string) => apiGet<import("../lib/material.ts").MaterialAnswer>(`/videos/${id}/material`),
  retentionCurve: (id: string) => apiGet<{ curves: { platform: string; postId: string; points: { second: number; retained: number }[] }[] }>(`/videos/${id}/retention-curve`),
  // (tf3-35b) an admin's dry run rides on the create (dryRunBeforeClips: true; the backend refuses it from anyone else)
  create: (body: CreateVideoRequest & { dryRunBeforeClips?: true }) =>
    apiPost<{ video: Video }>("/videos", body),

  list: (params: { limit?: number; offset?: number; channelId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.channelId) qs.set("channelId", params.channelId);
    const q = qs.toString();
    return apiGet<{ videos: Video[]; pagination: { limit: number; offset: number; count: number } }>(
      `/videos${q ? `?${q}` : ""}`,
    );
  },

  get: (id: string) =>
    apiGet<{ video: Video; segments: Segment[] }>(`/videos/${id}`),

  publish: (id: string, body: PublishVideoRequest) =>
    apiPost<{ ok: boolean; message: string }>(`/videos/${id}/publish`, body),

  selectThumbnail: (id: string, thumbnailUrl: string) =>
    apiRequest<{ ok: boolean }>(`/videos/${id}/thumbnail`, {
      method: "PATCH",
      body: { thumbnailUrl },
    }),

  delete: (id: string) =>
    apiDelete<{ ok: boolean }>(`/videos/${id}`),

  // ── Session 4 K2: run-backed (engine) videos ──
  /** The run's filmstrip (beats + words, frames, animatic, render, the open gate), customer view. */
  engine: (id: string) => apiGet<EngineView>(`/videos/${id}/engine`),
  // session 7 N5: answer the question the video waits on (TubeForge offers the topic-led angle)
  // (TF-6b-1) any choice the question lists, with the questionId it showed (required beyond the evidence pair)
  answerNeedsInput: (id: string, choice: string, questionId: string | null) =>
    apiPost<{ ok: boolean }>(`/videos/${id}/needs-input`, { choice, ...(questionId ? { questionId } : {}) }),
  /** Edit one beat's words at the script gate — the run re-plans from the words' first readers. */
  editBeatWords: (id: string, beatId: string, text: string) =>
    apiRequest<{ ok: boolean; replannedFrom: string[] }>(`/videos/${id}/script/beats/${encodeURIComponent(beatId)}`, { method: "PATCH", body: { text } }),
  /** The customer accepted the finished video (download): the run's final decision. */
  approveFinal: (id: string) => apiPost<{ ok: boolean; decided: boolean }>(`/videos/${id}/final/approve`, {}),

  // ── Strategy & Campaign ──

  generateStrategy: (profile: any) =>
    apiPost<any>("/videos/strategy", profile),

  listStrategies: () =>
    apiGet<{ strategies: any[] }>("/videos/strategies"),

  getStrategy: (id: string) =>
    apiGet<any>(`/videos/strategies/${id}`),

  discoverSegments: (profile: any) =>
    apiPost<any>("/videos/segments", profile),
  // (TF3-3) generateCampaign (POST /videos/campaign: legacy videos made outside the engine) is retired
};

/** Session 4 K2: the customer view of a run-backed video (backend GET /videos/:id/engine — the workers' filmstrip,
 *  operator fields removed). Only the fields the review pages read are typed. */
export interface EngineBeat {
  beatId: string; position: number; name: string | null; spokenText: string | null;
  previews?: { frame?: { thumbUrl?: string; posterUrl?: string } | null } | null;
  production?: { image?: { url?: string | null } | null;
    /** (B112) where the beat starts in the conformed timeline: the final review's change links (absent: no link) */
    conformedStartMs?: number | null } | null;
}
export interface EngineView {
  engine: boolean; runId?: string; status?: string;
  filmstrip?: {
    segments: { id: string; beats: EngineBeat[] }[];
    document?: { animaticUrl?: string | null; render?: { url: string } | null } | null;
    run?: { status: string; awaitingGate?: { stepId: string } | null;
      /** real progress from the run's step rows (workers' filmstrip) */
      progress?: { done: number; total: number; currentStepId: string | null; failed: number } | null } | null;
  };
}

// ── Session 4 L1/L3: channels (@vms/publishing under /api/publishing), delivery, and proposals ──
export interface PublishChannelView { channelId: string; channel: string; accountName: string; avatarUrl?: string; status: "active" | "expired" | "revoked" | "needs_reconnect"; statusReason?: string }
export interface Proposal { candidateId: string; rank: number; angle: string | null; script: string; previewUrl: string | null; chosen: boolean }
export const publishingApi = {
  channels: () => apiGet<{ channels: PublishChannelView[] }>("/publishing/channels"),
  /** Starts OAuth; the returned URL is opened in a popup and the channel list is polled until it appears. */
  /** Starts a connect (@vms/publishing 0.3.3): the returnUrl is this page — the connect completes here, in this session. */
  connect: (channel: string, returnUrl: string) => apiPost<{ authorizeUrl: string; state: string }>("/publishing/channels/connect", { channel, returnUrl }),
  /** The customer's publish tap on a run-backed video: the gate_final decision with the selected channels. */
  publishRunBacked: (videoId: string, channels: { channelId: string; channel: string }[]) =>
    apiPost<{ ok: boolean; message: string }>(`/videos/${videoId}/publish`, { channels }),
  /** "Post" on a delivered video — channels chosen now, the existing final video; reposts use the same path. */
  /** Session 6 W2: `scheduledAt` (ISO, in the future) schedules the Post; absent → post now. */
  /** (tf3-26) a TikTok target carries the post form's choices (lib/tiktok-post.ts); TikTok is never scheduled. */
  postLater: (videoId: string, channels: { channelId: string; channel: string; tiktok?: import("../lib/tiktok-post.ts").TikTokChoices }[], scheduledAt?: string) =>
    apiPost<{ ok: boolean; postId: string | null; postIds: string[]; skipped: { channel: string; reason: string }[]; message: string }>(`/videos/${videoId}/post`,
      { channels, ...(scheduledAt ? { scheduledAt } : {}) }),
  /** (tf3-26) the TikTok creator's settings, read fresh each time the post form opens (the package's route, no cache). */
  creatorInfo: (channelId: string) =>
    apiGet<import("../lib/tiktok-post.ts").CreatorInfo>(`/publishing/channels/${encodeURIComponent(channelId)}/creator-info`),
  /** (tf3-27) the TikTok post form's title prefill: the TikTok version's fitted caption, or null (the field starts empty). */
  tiktokTitle: (videoId: string) => apiGet<{ title: string | null }>(`/videos/${videoId}/tiktok-title`),
  /** (tf3-26) a post's targets as they change (queued | processing | published | failed). */
  postStatus: (videoId: string, postId: string) =>
    apiGet<{ postId: string; status: string; targets: { channelId: string; channel: string; publishStatus: string; failureReason: string | null; permalink: string | null }[] }>(
      `/videos/${videoId}/posts/${encodeURIComponent(postId)}/status`),
  /** Session 6 W1a.1: whether the video can be posted now and, if not, why — with its earlier cuts as history. */
  finishing: (videoId: string) => apiGet<{ finishing: VideoFinishing | null }>(`/videos/${videoId}/finishing`),
  proposals: (videoId: string) => apiGet<{ candidates: Proposal[] }>(`/videos/${videoId}/proposals`),
  choose: (videoId: string, candidateId: string) => apiPost<{ ok: boolean }>(`/videos/${videoId}/proposals/choose`, { candidateId }),
  // Session 6 T3: "Compare two openings" — the executions, the pick, a rejection (a new round)
  compare: (videoId: string) => apiGet<import("../components/engine/CompareExecutions").CompareData>(`/videos/${videoId}/compare`),
  compareChoose: (videoId: string, executionIndex: number) => apiPost<{ ok: boolean }>(`/videos/${videoId}/compare/choose`, { executionIndex }),
  compareReject: (videoId: string) => apiPost<{ ok: boolean }>(`/videos/${videoId}/compare/reject`, {}),
};
